import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Text, Toast, ToastBody, ToastTitle, Toaster, useId, useToastController } from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  ActivationOptionsResponse,
  InvitationActivateResponse,
  InvitationApiError,
  InvitationValidationResponse,
  useInvitationActivate,
  useInvitationActivationOptions,
  useInvitationValidate,
} from '@bingads-webui-campaign-react/accept-invitation-api';
import { getDebugErrorPage, getInvitationRouteContext } from '../query-params';
import { useAcceptInvitationTelemetry } from '../use-accept-invitation-telemetry';
import { useInvitationScreenActions } from '../use-invitation-screen-actions';
import { useInvitationFormLogger } from '../use-accept-invitation-logger';
import { ActivationConfirmation } from './activation-confirmation';
import { readPersistedActivation, writePersistedActivation, toPersistedValidation, markReauthAttempted, clearReauthAttempted } from './activation-persistence';
import { ActivationDeniedDialog } from './error-page/activation-denied-dialog';
import { ActivationErrorFallback } from './error-page/activation-error-fallback';
import { ActivationInvitationError } from './error-page/activation-invitation-error';
import { ActivationForm } from './activation-form';
import { buildConsentOnlyActivateRequest, debugInvitationReasonMap, formatErrorDetail, getTerminalInvitationReason, resolveSessionRecoveryUrl } from './accept-invitation-helpers';
import { ActivationAadOnlyRequired } from './error-page/activation-aad-only-required';
import { ActivationAlreadyHasAccess } from './error-page/activation-already-has-access';
import { ActivationAlternateAccountRequired } from './error-page/activation-alternate-account-required';
import { ActivationFacebookAccountRequired } from './error-page/activation-facebook-account-required';
import { ActivationGoogleAccountRequired } from './error-page/activation-google-account-required';
import { ActivationPersonalAccountRequired } from './error-page/activation-personal-account-required';
import { ActivationWorkAccountRequired } from './error-page/activation-work-account-required';
import { AfterPageReady } from './after-page-ready';
import { getDebugActivationOptions } from './debug-activation-options';
import { ExistingProfileDialog } from './error-page/existing-profile-dialog';
import { InvitationShell } from './invitation-shell';
import { LoadingState } from './loading-state';
import { useAcceptInvitationStyles } from './styles';

const happyPathActivationTypes = ['RegularActivation', 'MultiuserActivation'];

// Once an invitation has been activated, the code is consumed server-side — a second
// Validate/ActivationOptions call would fail. The activate response only lives in React
// state, so a browser refresh on the confirmation page would otherwise re-run Validate and
// surface an error page. To keep the confirmation page stable across refresh, we persist the
// activate result, the identity type ("Sign-in type" label), AND the Validate-derived fields the
// confirmation shows (customer/accounts/role) in sessionStorage, keyed by the invitation code, and
// restore them on mount — the confirmation no longer fetches AccessSummary, so this persisted copy is
// what lets a refresh render fully. See activation-persistence.

// @aiContributed-MAPFuse-2026-06-10
export type AcceptInvitationPageProps = {
  // Fired once when the first meaningful screen renders — i.e. when Validate + ActivationOptions
  // have resolved (`isLoading` becomes false) and the real profile form / error page / confirmation
  // replaces the loading state. The host uses this to complete the page-render perf marker. Optional
  // so tests and demos can omit it.
  onReady?: () => void;
};

export const AcceptInvitationPage: React.FC<AcceptInvitationPageProps> = ({ onReady }) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useAcceptInvitationStyles();
  const routeContext = useMemo(() => getInvitationRouteContext(), []);
  // UI debug override (?acceptinvitationdebug=googlerequired|facebookrequired|aadrequired|msarequired|
  // denied|existingprofile|fallbackerror): force a specific error page / dialog so it can be previewed
  // without reproducing the API failure.
  const debugErrorPage = useMemo(() => getDebugErrorPage(), []);
  // When debugging, drive the normal render path with a mocked ActivationOptions response (rather
  // than a parallel branch), so the debug param exercises the same code as a real API response.
  const debugActivationOptions = useMemo(() => getDebugActivationOptions(debugErrorPage), [debugErrorPage]);
  // Restore a prior successful activation (e.g. after a browser refresh on the confirmation page).
  const persistedActivation = useMemo(() => readPersistedActivation(routeContext.code), [routeContext.code]);
  const [activationResponse, setActivationResponse] = useState<InvitationActivateResponse | null>(persistedActivation?.activationResponse ?? null);
  // The sign-in provider (identityType) the confirmation page shows comes from the ActivationOptions
  // API. Once activation succeeds we stop calling ActivationOptions (the code is consumed), so its
  // live data is cleared — capture the identityType into state at activation time so the "You're all
  // set" page can still show it on the same-session render (not just after a refresh, where it is
  // restored from the persisted record). Seed from the persisted value for the refresh case.
  const [activatedIdentityType, setActivatedIdentityType] = useState<string | undefined>(persistedActivation?.identityType);
  // The Validate fields the confirmation page shows (customer/accounts/role). Once activation
  // succeeds we stop calling Validate (the code is consumed) and the hook clears its live `data` on
  // the next render (empty code -> reset). Capture the Validate data into state at activation time so
  // the "You're all set" page still has it on the same-session render (not just after a refresh,
  // where it is restored from the persisted record). Seed from the persisted value for the refresh
  // case.
  const [activatedValidation, setActivatedValidation] = useState<InvitationValidationResponse | undefined>(persistedActivation?.validation);
  // The Validate fields restored from the persisted record after a refresh (the confirmation no
  // longer fetches AccessSummary, and Validate can't be re-called on the consumed code).
  const persistedValidation = persistedActivation?.validation;

  // Skip Validate/ActivationOptions once activation has succeeded (this session or persisted across a
  // refresh): the invitation code is already consumed, so re-validating it would error out.
  // Also skip the live calls entirely when previewing a mocked error page via `acceptinvitationdebug`,
  // so the debug param doesn't issue real backend requests even with a real invitation code present.
  const shouldValidate = !debugErrorPage && Boolean(routeContext.code) && !activationResponse;
  const validationState = useInvitationValidate(shouldValidate ? routeContext.code : '');
  const activationOptionsCode = !debugErrorPage && validationState.data && !validationState.error && !activationResponse
    ? routeContext.code
    : '';
  const activationOptionsState = useInvitationActivationOptions(activationOptionsCode, routeContext.intent);
  const activateState = useInvitationActivate();

  const toasterId = useId('accept-invitation-toaster');
  const { dispatchToast } = useToastController(toasterId);

  // Effective ActivationOptions response: the mocked debug response when debugging, else live API
  // data. The debug param also bypasses the loading/error states (there is no real call to wait on).
  const activationOptionsData = debugActivationOptions || activationOptionsState.data;
  // ActivationOptions runs only after Validate resolves (activationOptionsCode is non-empty). Its hook
  // initializes isLoading=false while its code arg is still empty, so on the single render right after
  // Validate resolves — before the fetch effect flips isLoading to true — the query reports neither
  // loading, data, nor error. Without this guard that render falls through to the general error
  // fallback (a wrong screen that flashes before the real profile form / error page). Treat that
  // in-between window as loading so only the correct final screen is ever shown.
  const activationOptionsPending = Boolean(activationOptionsCode)
    && !activationOptionsState.data
    && !activationOptionsState.error;
  const isLoading = !debugErrorPage
    && (validationState.isLoading || activationOptionsState.isLoading || activationOptionsPending);
  const loadError = debugErrorPage ? undefined : (validationState.error || activationOptionsState.error);
  // A 401 from a session-bound API (ActivationOptions / Activate — NOT the anonymous Validate) means
  // the session is invalid/expired; we send the user to the logout page (effect below) instead of
  // rendering an error page.
  const sessionExpired = !debugErrorPage
    && (activationOptionsState.error?.status === 401 || activateState.error?.status === 401);

  // Not every 401 means the user is really signed out. `ReauthRequired` is the server telling us the
  // browser session is still valid but its own token cache lost this user's entry (a Redis/MSAL
  // miss). Signing the user out of that is a dead end — the invitation code is dropped and they have
  // to start over — whereas bouncing the BROWSER through the challenge endpoint the server hands
  // back re-mints the tokens against the still-live IdP session, usually with no prompt, and lands
  // back on this page with the code intact. Only a top-level navigation can do that; the failed
  // request was an XHR, which is exactly why the server cannot recover this on its own.
  const reauthUrlFromServer = [activationOptionsState.error, activateState.error]
    .find(error => error?.status === 401 && error?.error === 'ReauthRequired')?.authUrl;
  // The server's own code for the 401, reported as-is so the funnel can separate a recoverable
  // `ReauthRequired` from a real `NotAuthenticated` sign-out.
  const sessionExpiredErrorCode = [activationOptionsState.error, activateState.error]
    .find(error => error?.status === 401)?.error;
  // Resolve the recovery URL once per session-expired transition so the redirect effect and the
  // render path agree on a single value. See resolveSessionRecoveryUrl for the one-shot bounce rule.
  const sessionRecoveryUrl = useMemo(
    () => (sessionExpired ? resolveSessionRecoveryUrl(reauthUrlFromServer, routeContext.code) : ''),
    [sessionExpired, reauthUrlFromServer, routeContext.code]
  );
  const activationType = activationOptionsData?.type;
  // Happy path: a profile form, an auto-activated consent-only multi-user, or the existing-profile
  // dialog. Everything else routes to an error page (a specific one, else the general fallback).
  const isHappyPathActivation = happyPathActivationTypes.includes(activationType as string);
  // The invited email is registered with a Microsoft work/school (AAD) account, but the user signed
  // in with a personal Microsoft (MSA) or Google account. ActivationOptions reports this as
  // ActivateWithAlternate whose matched (expected) identity is AAD while the current identity is not.
  // They must re-authenticate with the work/school account, so we show a dedicated error page rather
  // than the generic "unsupported" fallback. (More ActivationOptions error pages will be added later.)
  const isWorkAccountRequired = activationType === 'ActivateWithAlternate'
    && activationOptionsData?.expectedIdentityType === 'AAD'
    && activationOptionsData?.currentIdentityType !== 'AAD';
  // The mirror case: the invited email is registered with a personal Microsoft (MSA) account, but
  // the user signed in with a work/school (AAD) or Google account. They must re-authenticate with
  // the personal account.
  const isPersonalAccountRequired = activationType === 'ActivateWithAlternate'
    && activationOptionsData?.expectedIdentityType === 'MSA'
    && activationOptionsData?.currentIdentityType !== 'MSA';
  // The invited email is registered with a Google account, but the user signed in with a personal
  // Microsoft (MSA) or work/school (AAD) account. They must re-authenticate with Google.
  const isGoogleAccountRequired = activationType === 'ActivateWithAlternate'
    && activationOptionsData?.expectedIdentityType === 'Google'
    && activationOptionsData?.currentIdentityType !== 'Google';
  // The invited email is registered with a Facebook account, but the user signed in with another
  // provider (MSA / AAD / Google). They must re-authenticate with Facebook.
  const isFacebookAccountRequired = activationType === 'ActivateWithAlternate'
    && activationOptionsData?.expectedIdentityType === 'Facebook'
    && activationOptionsData?.currentIdentityType !== 'Facebook';
  // Same-provider ActivateWithAlternate: the invited email maps to a DIFFERENT account of the SAME
  // identity provider the user signed in with (expected === current). The four pages above only
  // handle the cross-provider mismatch; without this the same-provider case falls through to the
  // generic "Something went wrong" fallback. Two sub-cases, keyed on `reason`:
  //   - Case 1 (`PuidMatch_MatchesNonLoginUserWhoseLoginUserCanBeMerged`): the user proved ownership
  //     of the matched account by signing in, so we reveal it and offer "Continue" (re-auth as it).
  //   - Case 2 (`AliasMatch_*`): ownership is NOT proven, so we neither reveal the matched account
  //     nor offer "Continue" — only "Use a different email".
  // Guarded on `expectedIdentityType` being present (so a missing type can't spuriously equal a
  // missing current one) — anything else falls to the generic fallback (per the server-side spec).
  const isAlternateSameProvider = activationType === 'ActivateWithAlternate'
    && Boolean(activationOptionsData?.expectedIdentityType)
    && activationOptionsData?.expectedIdentityType === activationOptionsData?.currentIdentityType;
  // Case 1 — needs `expectedUsername` to render the "continue as {{expectedUsername}}" copy; without
  // it we fall through to the generic fallback (per the spec's fallback rule).
  const isAlternateAccountMerge = isAlternateSameProvider
    && activationOptionsData?.reason === 'PuidMatch_MatchesNonLoginUserWhoseLoginUserCanBeMerged'
    && Boolean(activationOptionsData?.expectedUsername);
  // Case 2 — any AliasMatch_* reason (ownership not proven).
  const isAlternateAccountBlocked = isAlternateSameProvider
    && Boolean(activationOptionsData?.reason?.startsWith('AliasMatch_'));
  // AAD-enforced (aad-only) invitation: the inviting customer disallows personal Microsoft (MSA)
  // accounts — Validate reports invitationFlags.msaSupported === false. If the user signed in with a
  // known non-AAD identity they can't accept the invitation and must re-authenticate with a work or
  // school (AAD) account, so we show a dedicated error page. We only block when the current identity
  // is positively known to be non-AAD (so a missing identityType never blocks a legitimate user).
  // The `aadonly` debug scenario forces the page for preview.
  const currentIdentityType = activationOptionsData?.currentIdentityType || activationOptionsData?.identityType;
  // The signed-in identity shown in the top bar. Only ActivationOptions reports who is actually
  // authenticated — NOT serverContext.CurrentUser.Name (this user has no profile yet, which is the
  // point of the page) and NOT the Validate email (that is who the invitation was sent to, which is
  // a different identity in every ActivateWithAlternate case). Empty hides the control.
  const signedInUsername = activationOptionsData?.username
    || activationOptionsData?.currentUsername
    || '';
  const isAadOnlyAccountRequired = debugErrorPage === 'aadonly'
    || (validationState.data?.invitationFlags?.msaSupported === false
      && Boolean(currentIdentityType)
      && currentIdentityType !== 'AAD');
  // ActivationDenied: the signed-in identity exists but can't accept this invitation (the legacy
  // "identity blocked" outcome — the *CannotSignIn / *CannotBeMerged ResultCodes). The only path
  // forward is to sign in with a different email, so we show a single-action dialog.
  const isActivationDenied = activationType === 'ActivationDenied';
  // A more specific ActivationDenied: reason `PuidMatch_MatchesUserUnderSameCustomer` means the
  // signed-in identity ALREADY matches an existing user under the SAME customer — i.e. this email
  // already has access and the invitation is redundant. Nothing is wrong, so we show a dedicated
  // "already has access" page instead of the generic denied dialog. Checked BEFORE isActivationDenied.
  const isAlreadyHasAccess = isActivationDenied
    && activationOptionsData?.reason === 'PuidMatch_MatchesUserUnderSameCustomer';
  // UpgradeAndActivate is the legacy MSA->AAD migration offer. The new flow doesn't offer migration;
  // it reuses the personal-account page to ask the user to sign in with their existing personal
  // Microsoft (MSA) account (`fromUsername`) to accept the invitation.
  const isUpgradeAndActivate = activationType === 'UpgradeAndActivate';
  // MultiuserActivation: decide consent-only vs. form purely from the existing user's address in
  // the ActivationOptions response. The server only consumes the invitation when a complete master
  // data record (name + address line1/city/country) is posted. So:
  //   - existing user already has a usable address  -> skip the form, auto-activate by echoing it.
  //   - no address                                  -> render the form so the user provides one,
  //                                                     and the activate fires on form submit.
  const existingUserAddress = activationOptionsData?.existingUser?.address;
  const hasUsableExistingAddress = Boolean(existingUserAddress?.addressLine1 && existingUserAddress?.city && existingUserAddress?.country);
  const isConsentOnlyActivation = activationType === 'MultiuserActivation' && hasUsableExistingAddress;
  const logConsentDiagnostic = useInvitationFormLogger(Boolean(debugErrorPage), 'ConsentOnly');

  // When the user explicitly chose "Create an account" pre-auth (intent=create) but the signed-in
  // identity already maps to a known profile (the consent-only skip-form path), we must NOT silently
  // auto-activate. Instead we surface the "An existing profile was found" dialog so the user can
  // decide between reusing the profile or starting over with a different email. For every other
  // entry (no intent, or "Use an existing account") the consent-only path keeps auto-activating.
  // The `existingprofile` debug scenario is treated as create-intent so the dialog previews without
  // also needing `?intent=create` in the URL.
  const isCreateIntent = routeContext.intent === 'create' || debugErrorPage === 'existingprofile';
  const [useExistingProfileConfirmed, setUseExistingProfileConfirmed] = useState(false);
  const needsExistingProfileConsent = isConsentOnlyActivation && isCreateIntent;
  const showExistingProfileDialog = needsExistingProfileConsent && !useExistingProfileConfirmed && !activationResponse;
  // The consent-only path may activate automatically (no create-intent) or only after the user
  // confirms "Use existing profile" on the dialog (create-intent).
  const canConsentOnlyActivate = isConsentOnlyActivation && (!needsExistingProfileConsent || useExistingProfileConfirmed);

  // Signal the host once the FIRST MEANINGFUL screen is ready to paint — used to complete the
  // page-render perf marker so it measures time-to-first-real-screen. We intentionally exclude the
  // intermediate neutral screens that render a plain <LoadingState/> with no real content yet:
  //   - the initial Validate/ActivationOptions load (isLoading),
  //   - the session-expired redirect spinner (sessionExpired + sessionRecoveryUrl), and
  //   - the consent-only auto-activation spinner (a complete-profile MultiuserActivation activates
  //     behind a spinner before the confirmation appears — see renderContent's consent-only branch).
  // Once activation resolves (activationResponse set, or an activate error/alert, or the confirmation)
  // a real screen is shown and the marker completes. Fires once via a ref guard; useLayoutEffect so
  // it runs right after the committed DOM, matching the perf marker library's own done() timing.
  const isConsentOnlyAutoActivating = isConsentOnlyActivation
    && !loadError
    && !activationResponse
    && !activateState.error
    && !showExistingProfileDialog;
  const isNeutralLoadingScreen = isLoading
    || (sessionExpired && Boolean(sessionRecoveryUrl))
    || isConsentOnlyAutoActivating;
  const onReadyFiredRef = useRef(false);
  useLayoutEffect(() => {
    if (!isNeutralLoadingScreen && !onReadyFiredRef.current) {
      onReadyFiredRef.current = true;
      onReady?.();
    }
  }, [isNeutralLoadingScreen, onReady]);

  // Post-auth telemetry: emits the entry (PageLoaded), the resolved outcome (which screen / error),
  // the confirmation, and the Activate success/failure lines through the shared instrumentation
  // pipeline (mirroring the server-side raw logs), so the success-rate + error-count reports can be
  // built per session (SesID) or per invitation code. See use-accept-invitation-telemetry.
  const telemetry = useAcceptInvitationTelemetry({
    code: routeContext.code,
    debugErrorPage: Boolean(debugErrorPage),
    isCreateIntent,
    isLoading,
    sessionExpired,
    sessionExpiredErrorCode,
    validationError: validationState.error,
    activationOptionsError: activationOptionsState.error,
    activationOptionsData,
    currentIdentityType,
    activationResponse,
    isAadOnlyAccountRequired,
    isHappyPathActivation,
    isConsentOnlyActivation,
    showExistingProfileDialog,
    isWorkAccountRequired,
    isPersonalAccountRequired,
    isGoogleAccountRequired,
    isFacebookAccountRequired,
    isAlternateAccountMerge,
    isAlternateAccountBlocked,
    isActivationDenied,
    isAlreadyHasAccess,
    isUpgradeAndActivate,
  });

  // Persist the activate result so a refresh on the confirmation page restores it instead of
  // re-running Validate (which would fail on the now-consumed code).
  const persistAndSetActivationResponse = useCallback((response: InvitationActivateResponse) => {
    const identityType = activationOptionsState.data?.identityType;
    writePersistedActivation(routeContext.code, {
      activationResponse: response,
      identityType,
      validation: toPersistedValidation(validationState.data),
    });
    // The canonical "flow finished" signal (report numerator) — every activate path funnels here.
    telemetry.logActivateSuccess(response, identityType);
    setActivatedIdentityType(identityType);
    setActivatedValidation(validationState.data);
    setActivationResponse(response);
  }, [activationOptionsState.data?.identityType, routeContext.code, telemetry, validationState.data]);

  const confirmUseExistingProfile = useCallback(() => setUseExistingProfileConfirmed(true), []);

  // Every error page / dialog button, plus the profile form's sign-out. Each emits its telemetry
  // before navigating — see use-invitation-screen-actions.
  const {
    handleUseDifferentEmail,
    handleGoToAccount,
    handleUseExistingProfile,
    handleContinueWithWorkAccount,
    handleContinueWithPersonalAccount,
    handleSignInWithGoogle,
    handleSignInWithFacebook,
    handleContinueAsAlternateAccount,
    handleBackToInvitation,
    handleProfileFormSignOut,
  } = useInvitationScreenActions({
    telemetry,
    code: routeContext.code,
    expectedUsername: activationOptionsData?.expectedUsername,
    expectedIdentityType: activationOptionsData?.expectedIdentityType,
    customerId: validationState.data?.customerId,
    onUseExistingProfileConfirmed: confirmUseExistingProfile,
  });

  const showApiErrorToast = useCallback((apiName: string, error: InvitationApiError) => {
    dispatchToast(
      <Toast>
        <ToastTitle>{i18n.getString(_TL_('{{apiName}} request failed'), { apiName })}</ToastTitle>
        <ToastBody>{formatErrorDetail(error)}</ToastBody>
      </Toast>,
      { intent: 'error', timeout: 10000 }
    );
  }, [dispatchToast, i18n]);

  const lastNotifiedRef = useRef<{ activate?: InvitationApiError }>({});
  const autoActivateRef = useRef(false);
  const logoutRedirectedRef = useRef(false);

  // A 401 on a session-bound API means the session is unusable — navigate away to recover it: to the
  // server's re-auth challenge when it sent one (`ReauthRequired`, silent token repair), otherwise to
  // the logout page so the user can sign out and start over. Guarded so we navigate at most once.
  useEffect(() => {
    if (!sessionExpired || logoutRedirectedRef.current) {
      return;
    }
    if (sessionRecoveryUrl) {
      logoutRedirectedRef.current = true;
      // Persist the attempt before navigating — logoutRedirectedRef is in-memory and does not
      // survive the round-trip, so it cannot cap the bounce on its own.
      if (reauthUrlFromServer) {
        markReauthAttempted(routeContext.code);
      }
      window.location.assign(sessionRecoveryUrl);
    }
  }, [sessionExpired, sessionRecoveryUrl, reauthUrlFromServer, routeContext.code]);

  // A successful ActivationOptions means the token cache is healthy again, so the one-shot re-auth
  // marker has served its purpose — clearing it caps the guard at one bounce per failure episode
  // rather than one per tab.
  useEffect(() => {
    if (activationOptionsState.data) {
      clearReauthAttempted(routeContext.code);
    }
  }, [activationOptionsState.data, routeContext.code]);

  // A failed Activate (form submit or consent-only auto-activate) is ALWAYS logged so the error-count
  // report never loses it — including a 401, which additionally redirects to the logout page (the
  // session-expired effect above) and gets no retry toast. Every other failure surfaces a retry toast.
  // Validate / ActivationOptions failures and unhandled activation types render the general error page.
  // TODO: route a fraud/blocked Activate failure (errorCodes contains CCMTErrorNumber_FraudUserIsBlocked)
  // to a dedicated non-retryable "account blocked" error page instead of the generic retryable toast,
  // once that error-page design is ready.
  useEffect(() => {
    if (activateState.error && lastNotifiedRef.current.activate !== activateState.error) {
      lastNotifiedRef.current.activate = activateState.error;
      // Log first (including 401) so the terminal failure line is emitted before any logout redirect.
      telemetry.logActivateFailed(activateState.error, activationOptionsState.data?.identityType);
      // No retry toast for a 401 (redirects to logout) or a terminal invitation-code error (we render
      // the dedicated friendly reason page instead of a raw, non-retryable toast).
      if (activateState.error.status !== 401 && !getTerminalInvitationReason(activateState.error)) {
        showApiErrorToast('Invitation/Activate', activateState.error);
      }
    }
  }, [activateState.error, activationOptionsState.data?.identityType, showApiErrorToast, telemetry]);

  // @aiContributed-MAPFuse-2026-06-15
  // Consent-only path: the existing user already has a complete address, so we don't render a
  // form. Activate once, automatically, echoing the existing user's master data (name + address)
  // into the userModel — mirroring exactly what the legacy pre-filled profile form posts on Save.
  // Sending an empty/addressless userModel makes the MT resolve the user by PUID and return 200
  // without actually consuming this invitation, so we must carry the existing address forward.
  // Then the confirmation page renders from activationResponse.
  useEffect(() => {
    if (!canConsentOnlyActivate || autoActivateRef.current || activationResponse) {
      return;
    }
    autoActivateRef.current = true;
    (async () => {
      try {
        const request = buildConsentOnlyActivateRequest(routeContext, activationOptionsState.data);
        logConsentDiagnostic({ event: 'ActivateRequestStarted' });
        const response = await activateState.activate(request);
        persistAndSetActivationResponse(response);
      } catch {
        // Error is stored in activateState; the error toast and inline alert render from it.
      }
    })();
  }, [canConsentOnlyActivate, activationResponse, logConsentDiagnostic, activateState, activationOptionsState.data, persistAndSetActivationResponse, routeContext]);

  const renderContent = () => {
    // Neutral loading chrome: a centered spinner on the plain background used by the error and
    // confirmation pages — NOT the profile shell (which shows the "Create your user profile" header
    // and the step rail). Until ActivationOptions resolves we don't know the outcome (profile form,
    // an error page, the confirmation, or a dialog), so we must not flash the profile-page chrome.
    if (isLoading) {
      return (
        <div className={classes.centeredPage}>
          <LoadingState />
        </div>
      );
    }

    // A 401 (unusable session) redirects to the re-auth challenge or the logout page (effect above);
    // show the loading state while the browser navigates so the user doesn't see a flash of the error
    // fallback. If neither URL is available, the effect can't navigate, so fall back to the general
    // error page instead of spinning indefinitely.
    if (sessionExpired) {
      if (sessionRecoveryUrl) {
        return (
          <div className={classes.centeredPage}>
            <LoadingState />
          </div>
        );
      }
      return <ActivationErrorFallback onBackToInvitation={handleBackToInvitation} />;
    }

    // A terminal invitation-code error (invalid / expired / user-limit / inactive) from the anonymous
    // Validate OR the post-auth Activate — render the friendly reason page (matching the pre-auth
    // InvitationErrorView copy) instead of the raw CCMT fault on the form or the generic fallback.
    // Transient/retryable errors return null here and keep their existing rendering. The
    // `acceptinvitationdebug` reason aliases force a specific page for local preview.
    const terminalReason = (debugErrorPage && debugInvitationReasonMap[debugErrorPage])
      || getTerminalInvitationReason(validationState.error)
      || getTerminalInvitationReason(activateState.error);
    if (terminalReason) {
      return <ActivationInvitationError reason={terminalReason} onBackToInvitation={handleBackToInvitation} />;
    }

    // AAD-only invitation accepted with a non-AAD identity — block with a dedicated error page
    // BEFORE the happy path (the server may otherwise report a normal RegularActivation) so the user
    // is directed to re-authenticate with a Microsoft work/school account.
    if (!loadError && isAadOnlyAccountRequired) {
      return (
        <ActivationAadOnlyRequired
          customerName={validationState.data?.customerName}
          onContinueWithMicrosoft={handleContinueWithWorkAccount}
          onGoBack={handleBackToInvitation}
        />
      );
    }

    // 1) Happy path — collect the profile (form), auto-activate a consent-only multi-user, or show
    //    the "An existing profile was found" dialog.
    if (!loadError && isHappyPathActivation) {
      if (isConsentOnlyActivation) {
        if (activateState.error) {
          return (
            <InvitationShell>
              <div className={classes.alert} role="alert">
                <Text block>{activateState.error.message || activateState.error.error || i18n.getString(_TL_('Something went wrong.'))}</Text>
              </div>
            </InvitationShell>
          );
        }
        // Create-intent: waiting for the user to decide on the existing-profile dialog. Render it
        // over a plain neutral backdrop — not the "Create your user profile" shell chrome.
        if (showExistingProfileDialog) {
          return (
            <>
              <div className={classes.dialogBackdrop} />
              <ExistingProfileDialog
                open
                customerName={validationState.data?.customerName}
                isBusy={activateState.isLoading}
                onUseExistingProfile={handleUseExistingProfile}
                onUseDifferentEmail={handleUseDifferentEmail}
              />
            </>
          );
        }
        // Otherwise the consent-only path auto-activates and then lands on the confirmation page (it
        // never shows the profile form), so render a neutral centered spinner while it runs rather
        // than the "Create your user profile" shell chrome.
        return (
          <div className={classes.centeredPage}>
            <LoadingState />
          </div>
        );
      }
      return (
        <>
          <InvitationShell userName={signedInUsername} onSignOut={handleProfileFormSignOut}>
            <ActivationForm
              key={JSON.stringify([routeContext.code, signedInUsername, currentIdentityType])}
              debug={Boolean(debugErrorPage)}
              routeContext={routeContext}
              validation={validationState.data}
              activationOptions={activationOptionsData}
              isSubmitting={activateState.isLoading}
              error={activateState.error}
              activate={activateState.activate}
              onSubmit={persistAndSetActivationResponse}
            />
          </InvitationShell>
          {/* While the Activate request from the profile form is in flight, cover the page with a
              neutral loading screen. The form stays mounted underneath so a failed Activate returns
              to it with the entered values intact (plus the error banner); a successful Activate sets
              activationResponse and the whole page is replaced by the confirmation view above. */}
          {activateState.isLoading && (
            <div className={classes.loadingOverlay}>
              <LoadingState />
            </div>
          )}
        </>
      );
    }

    // 2) Specific error pages.
    if (isWorkAccountRequired) {
      return (
        <ActivationWorkAccountRequired
          email={activationOptionsData?.expectedUsername
            || validationState.data?.email
            || routeContext.email}
          onContinueWithMicrosoft={handleContinueWithWorkAccount}
          onGoBack={handleBackToInvitation}
        />
      );
    }

    if (isPersonalAccountRequired) {
      return (
        <ActivationPersonalAccountRequired
          email={activationOptionsData?.expectedUsername
            || validationState.data?.email
            || routeContext.email}
          onContinueWithMicrosoft={handleContinueWithPersonalAccount}
          onGoBack={handleBackToInvitation}
        />
      );
    }

    if (isGoogleAccountRequired) {
      return (
        <ActivationGoogleAccountRequired
          email={activationOptionsData?.expectedUsername
            || validationState.data?.email
            || routeContext.email}
          onSignInWithGoogle={handleSignInWithGoogle}
          onGoBack={handleBackToInvitation}
        />
      );
    }

    if (isFacebookAccountRequired) {
      return (
        <ActivationFacebookAccountRequired
          email={activationOptionsData?.expectedUsername
            || validationState.data?.email
            || routeContext.email}
          onSignInWithFacebook={handleSignInWithFacebook}
          onGoBack={handleBackToInvitation}
        />
      );
    }

    // Same-provider ActivateWithAlternate — the invited email maps to a DIFFERENT account of the SAME
    // provider. Case 1 (ownership proven) reveals the matched account and offers "Continue"; Case 2
    // (AliasMatch, ownership not proven) is a dead-end with only "Use a different email". Anything
    // else (missing expected fields / other reason) falls through to the general fallback below.
    if (isAlternateAccountMerge) {
      return (
        <ActivationAlternateAccountRequired
          variant="canMerge"
          currentUsername={activationOptionsData?.currentUsername
            || validationState.data?.email
            || routeContext.email}
          expectedUsername={activationOptionsData?.expectedUsername}
          onContinue={handleContinueAsAlternateAccount}
          onUseDifferentEmail={handleUseDifferentEmail}
        />
      );
    }

    if (isAlternateAccountBlocked) {
      return (
        <ActivationAlternateAccountRequired
          variant="blocked"
          onUseDifferentEmail={handleUseDifferentEmail}
        />
      );
    }

    // Already has access (ActivationDenied + PuidMatch_MatchesUserUnderSameCustomer) — the invited
    // email already has access to the account, so show the dedicated page with two paths forward:
    // retry with a different email, or go straight into the account. Checked BEFORE the general
    // ActivationDenied branch (which would otherwise catch it as a generic "blocked").
    if (isAlreadyHasAccess) {
      return (
        <ActivationAlreadyHasAccess
          onUseDifferentEmail={handleUseDifferentEmail}
          onGoToAccount={handleGoToAccount}
        />
      );
    }

    // ActivationDenied — the account can't accept this invitation; offer only "Use a different email".
    // Rendered as a dialog over a plain backdrop, mirroring the existing-profile dialog.
    if (isActivationDenied) {
      return (
        <>
          <div className={classes.dialogBackdrop} />
          <ActivationDeniedDialog
            open
            email={activationOptionsData?.username
              || validationState.data?.email
              || routeContext.email}
            onUseDifferentEmail={handleUseDifferentEmail}
          />
        </>
      );
    }

    // UpgradeAndActivate (legacy MSA->AAD migration) — reuse the personal-account page: ask the user
    // to sign in with their existing personal Microsoft (MSA) account (`fromUsername`).
    if (isUpgradeAndActivate) {
      return (
        <ActivationPersonalAccountRequired
          email={activationOptionsData?.fromUsername
            || validationState.data?.email
            || routeContext.email}
          onContinueWithMicrosoft={handleContinueWithPersonalAccount}
          onGoBack={handleBackToInvitation}
        />
      );
    }

    // 3) General fallback — unhandled activation types (incl. Unknown), a Validate/ActivationOptions
    //    400/500 failure, or a missing code. The single action signs the user out and returns to the
    //    invitation choice view.
    return <ActivationErrorFallback onBackToInvitation={handleBackToInvitation} />;
  };

  if (activationResponse) {
    // The "Sign-in type" comes from the ActivationOptions API's identityType. Once activation
    // succeeds we no longer call ActivationOptions (its live data is cleared), so use the value we
    // captured into state at activation time (also restored from the persisted record after a
    // refresh). This is the provider the user actually signed in with — never derived from the URL.
    const confirmationActivationOptions: ActivationOptionsResponse | undefined = activationOptionsState.data
      || (activatedIdentityType
        ? { type: 'Unknown', identityType: activatedIdentityType }
        : undefined);
    return (
      <>
        <AfterPageReady />
        <ActivationConfirmation
          activationResponse={activationResponse}
          validation={activatedValidation || validationState.data || persistedValidation}
          activationOptions={confirmationActivationOptions}
          onUnmappedRole={telemetry.logUnmappedRole}
        />
        <Toaster toasterId={toasterId} position="top-end" />
      </>
    );
  }

  return (
    <>
      <AfterPageReady />
      {renderContent()}
      <Toaster toasterId={toasterId} position="top-end" />
    </>
  );
};
